import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import type { BillableItem } from '@/lib/schemas';
import { toast } from 'sonner';
import { invalidateProjectListQueries } from '@/lib/cache-utils';
import { Info } from 'lucide-react';
import { useProjectCategoryOptions, useNextProjectNumberPreview } from '@/hooks';

interface ConvertProposalModalProps {
  open: boolean;
  onClose: () => void;
  projectId: string;  // Phase 7.5.13: Use UUID instead of project_number
  projectName: string;
  billableItems: BillableItem[];
  initialCategory?: string | null; // Phase 9.6b: Pre-populate category from proposal
}

type ItemDecision = 'confirm' | 'decline';

interface ItemDecisionState {
  [itemId: string]: ItemDecision;
}

export function ConvertProposalModal({
  open,
  onClose,
  projectId,
  projectName,
  billableItems,
  initialCategory,
}: ConvertProposalModalProps) {
  const queryClient = useQueryClient();

  // Batch 7.3.4: Show ALL items (including confirmed ones from before the fix)
  // This allows converting proposals that had items added before the fix
  const proposedItems = billableItems;

  // Initialize all proposed items as 'confirm' by default
  const initialDecisions: ItemDecisionState = {};
  proposedItems.forEach((item) => {
    initialDecisions[item.id] = 'confirm';
  });

  const [itemDecisions, setItemDecisions] = useState<ItemDecisionState>(initialDecisions);
  const [feeAssumptions, setFeeAssumptions] = useState('');
  const [updateAcceptedTimestamp, setUpdateAcceptedTimestamp] = useState(true);

  // Phase 9.6b: Category and project number state
  const [selectedCategory, setSelectedCategory] = useState<string | null>(initialCategory || null);
  const [customProjectNumber, setCustomProjectNumber] = useState<string>('');
  const [useCustomNumber, setUseCustomNumber] = useState(false);

  // Fetch category options
  const { data: projectCategoryOptions } = useProjectCategoryOptions();

  // Get category ID for preview
  const selectedCategoryOption = projectCategoryOptions?.find(c => c.value === selectedCategory);
  const selectedCategoryId = selectedCategoryOption?.id;

  // Get project number preview based on selected category
  const { data: previewNumber } = useNextProjectNumberPreview(selectedCategoryId);

  // Reset category when modal opens
  useEffect(() => {
    if (open) {
      setSelectedCategory(initialCategory || null);
      setCustomProjectNumber('');
      setUseCustomNumber(false);
    }
  }, [open, initialCategory]);

  // BUGFIX: Reset item decisions when billableItems prop changes
  // This ensures the modal state updates if billableItems loads after the modal opens
  useEffect(() => {
    const newDecisions: ItemDecisionState = {};
    proposedItems.forEach((item) => {
      newDecisions[item.id] = 'confirm';
    });
    setItemDecisions(newDecisions);
  }, [billableItems]);

  const handleToggleItem = (itemId: string) => {
    setItemDecisions((prev) => ({
      ...prev,
      [itemId]: prev[itemId] === 'confirm' ? 'decline' : 'confirm',
    }));
  };

  const convertMutation = useMutation({
    mutationFn: async () => {
      // Format item_decisions for API
      const item_decisions = Object.entries(itemDecisions).map(([item_id, action]) => ({
        item_id,
        action,
      }));

      // Phase 9.6b: Determine final project number
      const finalProjectNumber = useCustomNumber ? customProjectNumber.trim() : undefined;

      return api.projects.convertProposal(projectId, {
        item_decisions,
        fee_assumptions: feeAssumptions || undefined,
        update_accepted_timestamp: updateAcceptedTimestamp,
        category: selectedCategory || undefined,
        project_number: finalProjectNumber || undefined,
      });
    },
    onSuccess: async () => {
      toast.success(`${projectName} has been converted to a project`);

      // CRITICAL: await invalidation before closing modal to prevent stale data
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['projects', 'detail', projectId] }),
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey[0] === 'billable-items',
        }),
      ]);
      // Also invalidate list queries (non-blocking)
      invalidateProjectListQueries(queryClient);

      onClose();
    },
    onError: (error: Error) => {
      // Generic handler is sufficient here: fetchAPI (api.ts) sets error.message
      // from the backend's 'detail' field, so 409 collision responses surface as
      // "Project number collision — please try again..." without needing explicit
      // status checking (unlike useCreateProject which needs the suggestion field).
      toast.error(error.message || 'Failed to convert proposal');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    convertMutation.mutate();
  };

  const confirmedCount = Object.values(itemDecisions).filter((d) => d === 'confirm').length;
  const declinedCount = Object.values(itemDecisions).filter((d) => d === 'decline').length;

  // Calculate monetary totals
  const confirmedTotal = proposedItems
    .filter(item => itemDecisions[item.id] === 'confirm')
    .reduce((sum, item) => sum + item.amount, 0);

  const declinedTotal = proposedItems
    .filter(item => itemDecisions[item.id] === 'decline')
    .reduce((sum, item) => sum + item.amount, 0);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Convert Proposal to Project</DialogTitle>
          <DialogDescription>
            Review and confirm which items to include in the project. Items marked as confirmed will have their
            status updated to "confirmed", while declined items will be marked as "declined".
          </DialogDescription>
        </DialogHeader>

        {/* Phase 7.5.10: Acceptance timestamp notice */}
        <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
          <Info className="h-4 w-4 text-sky-600 mt-0.5 flex-shrink-0" />
          <span className="text-blue-800">
            This will mark the proposal as accepted with today's date and time.
          </span>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <h3 className="font-medium text-sm mb-2">Project: {projectName}</h3>
            {proposedItems.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No billable items in this proposal. You can still convert it to start tracking project progress.
              </p>
            ) : (
              <>
                <div className="text-sm text-muted-foreground mb-4">
                  Select items to include in the project ({confirmedCount} confirmed, {declinedCount} declined)
                </div>

                <div className="flex justify-between items-center p-4 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                  <div>
                    <div className="font-medium">Project Value</div>
                    <div className="text-sm text-muted-foreground">
                      {confirmedCount} items confirmed, {declinedCount} declined
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-blue-900">
                      ${confirmedTotal.toFixed(2)}
                    </div>
                    {declinedCount > 0 && (
                      <div className="text-sm text-muted-foreground line-through">
                        ${(confirmedTotal + declinedTotal).toFixed(2)}
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  {proposedItems.map((item) => {
                    const isConfirmed = itemDecisions[item.id] === 'confirm';
                    const isOptional = item.item_status === 'proposed-optional';

                    return (
                      <div
                        key={item.id}
                        className={`flex items-start space-x-3 p-3 border rounded-lg ${
                          isConfirmed ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <Checkbox
                          id={`item-${item.id}`}
                          checked={isConfirmed}
                          onCheckedChange={() => handleToggleItem(item.id)}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <Label
                            htmlFor={`item-${item.id}`}
                            className="font-normal cursor-pointer"
                          >
                            <div className="flex items-center gap-2 flex-wrap">
                              <span>{item.description}</span>
                              {/* Batch 7.3.4: Show current item_status */}
                              {isOptional && (
                                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                                  Optional
                                </span>
                              )}
                              {item.item_status === 'confirmed' && (
                                <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
                                  Already Confirmed
                                </span>
                              )}
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                item.category === 'professional_fee'
                                  ? 'bg-purple-100 text-purple-800'
                                  : 'bg-green-100 text-green-800'
                              }`}>
                                {item.category === 'professional_fee' ? 'Professional Fee' : 'Add Service'}
                              </span>
                            </div>
                            <div className="text-sm text-muted-foreground mt-1">
                              ${item.amount.toFixed(2)}
                            </div>
                          </Label>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* Phase 9.6b: Category and Project Number Selection */}
          <div className="space-y-4 border-t pt-4">
            <h4 className="font-medium text-sm">Project Assignment</h4>

            {/* Category Selection */}
            <div className="space-y-2">
              <Label htmlFor="category" className="text-sm font-medium">
                Category <span className="text-red-500">*</span>
              </Label>
              <Select
                value={selectedCategory || ''}
                onValueChange={(value) => {
                  setSelectedCategory(value || null);
                  // Reset custom number when category changes
                  setCustomProjectNumber('');
                  setUseCustomNumber(false);
                }}
              >
                <SelectTrigger id="category" data-testid="convert-category-select">
                  <SelectValue placeholder="Select a category..." />
                </SelectTrigger>
                <SelectContent>
                  {projectCategoryOptions?.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.value}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Category determines the project number prefix (e.g., A-47 for Architectural)
              </p>
            </div>

            {/* Project Number Preview/Edit */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Project Number
              </Label>

              {!useCustomNumber ? (
                <div className="flex items-center gap-3">
                  <div className="flex-1 p-3 bg-slate-100 border border-slate-200 rounded-lg">
                    <div className="text-sm text-muted-foreground">Auto-assigned number:</div>
                    <div className="text-lg font-mono font-semibold" data-testid="convert-preview-number">
                      {selectedCategory && previewNumber ? previewNumber : (
                        <span className="text-muted-foreground italic">Select a category first</span>
                      )}
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setUseCustomNumber(true);
                      setCustomProjectNumber(previewNumber || '');
                    }}
                    disabled={!selectedCategory}
                  >
                    Customize
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <Input
                    value={customProjectNumber}
                    onChange={(e) => setCustomProjectNumber(e.target.value)}
                    placeholder="Enter custom project number..."
                    className="flex-1 font-mono"
                    data-testid="convert-custom-number-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setUseCustomNumber(false);
                      setCustomProjectNumber('');
                    }}
                  >
                    Use Auto
                  </Button>
                </div>
              )}

              {!selectedCategory && (
                <p className="text-xs text-amber-600">
                  ⚠️ Please select a category to generate a project number
                </p>
              )}
            </div>
          </div>

          {/* Optional Fields */}
          {proposedItems.length > 0 && (
            <div className="space-y-4 border-t pt-4">
              <div>
                <Label htmlFor="fee-assumptions" className="text-sm font-medium">
                  Fee Assumptions (Optional)
                </Label>
                <Textarea
                  id="fee-assumptions"
                  placeholder="Enter any assumptions about fees, scope, or conditions..."
                  value={feeAssumptions}
                  onChange={(e) => setFeeAssumptions(e.target.value)}
                  className="mt-1.5"
                  rows={3}
                />
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="update-timestamp"
                  checked={updateAcceptedTimestamp}
                  onCheckedChange={(checked) => setUpdateAcceptedTimestamp(checked as boolean)}
                />
                <Label htmlFor="update-timestamp" className="text-sm font-normal cursor-pointer">
                  Update proposal accepted timestamp (uncheck to preserve original date on reconversion)
                </Label>
              </div>
            </div>
          )}

          {/* Confirmation Summary */}
          {proposedItems.length > 0 && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <h4 className="font-medium text-sm mb-3">Conversion Summary</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Items to confirm:</span>
                  <span className="font-medium text-emerald-700">{confirmedCount} (${confirmedTotal.toFixed(2)})</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Items to decline:</span>
                  <span className="font-medium text-gray-600">{declinedCount} (${declinedTotal.toFixed(2)})</span>
                </div>
                <div className="flex justify-between pt-2 border-t">
                  <span className="font-medium">Final Project Value:</span>
                  <span className="font-bold text-lg">${confirmedTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Phase 7.3.4 Improvement #5: Allow conversion with 0 items, but warn appropriately */}
          {proposedItems.length === 0 ? (
            <div className="text-sm text-sky-600 bg-blue-50 border border-blue-200 rounded p-3">
              ℹ️ This proposal has no billable items. You can still convert it to track project progress,
              scopes, and permits. Add billable items later to track project value.
            </div>
          ) : confirmedCount === 0 ? (
            <div className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded p-3">
              ⚠️ Warning: All items will be declined. The project will have no billable items (total value: $0.00).
            </div>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={convertMutation.isPending}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={convertMutation.isPending || !selectedCategory}
              data-testid="convert-proposal-submit"
            >
              {convertMutation.isPending ? 'Converting...' : 'Convert to Project'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
